import React from 'react';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { zeigtOmop, zeigtOpenEhr } from '../utils/helpers';
import ParameterRow from './ParameterRow';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
}));

/**
 * Data Catalog Table Component
 * Renders the complete parameter table with header and body
 *
 * @param {Array} parameters - Sorted parameters to display
 * @param {string} nameView - Ansicht: 'charite' | 'omop' | 'openehr' | 'alle' (#782)
 * @param {Array} expandedRows - Array of expanded parameter IDs
 * @param {Function} onRowClick - Handler for row click
 * @param {Function} onFavoriteToggle - Handler for favorite toggle
 * @param {Function} handleOpenListDialog - Handler for list dialog
 * @param {string} orderBy - Column to sort by
 * @param {string} order - Sort order ('asc' or 'desc')
 * @param {Function} handleRequestSort - Handler for sort request
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} getParameterListStatus - Get list status for parameter
 * @param {Function} isParameterInAnyList - Check if parameter is in any list
 * @param {Function} handleRemoveFromList - Remove parameter from list
 * @param {Function} handleAddToList - Add parameter to list
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} getPrimaryDescription - Get primary description
 * @param {Object} subtlePulse - Animation for deletion requests
 */
const DataCatalogTable = ({
  parameters,
  laedt = false,
  // **Getrennt von `laedt`, und das ist der Punkt** (#191, Review Runde 3).
  // Im Vollsatz-Modus haengt der Dienst ~10 Seiten aneinander und zeigt die
  // erste, sobald sie da ist. Danach ist `laedt` falsch -- die restlichen
  // Seiten laufen aber noch. Passierte keine Zeile der ersten Seite den
  // lokalen Filter, stand dort "Keine Parameter gefunden", waehrend die
  // Antwort noch unterwegs war: genau der Zustand, gegen den #191 geschrieben
  // ist, nur eine Ecke weiter.
  nachladen = false,
  skelettzeilen = 8,
  leerText = '',
  nameView,
  expandedRows,
  onRowClick,
  onFavoriteToggle,
  handleOpenListDialog,
  orderBy,
  order,
  handleRequestSort,
  activeWorkingList,
  parameterLists,
  getParameterListStatus,
  isParameterInAnyList,
  handleRemoveFromList,
  handleAddToList,
  allTagsWithStats,
  getIconComponent,
  getPrimaryDescription,
  subtlePulse,
}) => {
  const { t } = useTranslation();

  // Aufklappen, Parameter, Tags, Haeufigkeit, Sensitivitaet, Aktionen -- dazu
  // OMOP-Status ausser in der Charite-Ansicht und die Listenspalten A/B. Die Zahl
  // haelt Skelett- und Leerzeile ueber die ganze Breite; ein zu kleines `colSpan`
  // schiebt die Tabelle sonst sichtbar zusammen.
  // Je Stellung eine Spalte, in "alle" beide nebeneinander -- das ist die
  // einzige Ansicht, in der sich die zwei Darstellungen eines Parameters
  // vergleichen lassen (#782 AK2).
  const omopSpalte = zeigtOmop(nameView);
  const openehrSpalte = zeigtOpenEhr(nameView);

  const spaltenzahl =
    6 +
    (omopSpalte ? 1 : 0) +
    (openehrSpalte ? 1 : 0) +
    (activeWorkingList.length === 1 ? 1 : 0) +
    (activeWorkingList.length === 2 ? 2 : 0);

  return (
    // Die Breitenbegrenzung selbst sitzt eine Ebene hoeher: MainLayout gibt
    // der Hauptflaeche `minWidth: 0`, damit sie als Flex-Element unter die
    // Breite ihres Inhalts schrumpfen kann (#274, MainLayout.js). Ohne das
    // waechst diese Ebene nie ueber ihren Platz hinaus -- `maxWidth: '100%'`
    // hier waere wirkungslos, MUI setzt den Container ohnehin auf
    // `width: 100%` seines (dann korrekt begrenzten) Elternelements (Review
    // Runde 1, B2). `overflow-x: auto` ist deshalb explizit gepinnt statt dem
    // ungepinnten MUI-Default ueberlassen (Review Runde 1, B1): passt die
    // Tabelle trotzdem nicht in den verfuegbaren Platz, scrollt sie in sich
    // selbst (AK3), statt die Seite aufzuweiten.
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            {/* Single List Column */}
            {activeWorkingList.length === 1 && (
              <StyledTableCell width={45} align="center">
                <TableSortLabel
                  active={orderBy === 'lists'}
                  direction={orderBy === 'lists' ? order : 'asc'}
                  onClick={() => handleRequestSort('lists')}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '& .MuiTableSortLabel-icon': {
                      position: 'absolute',
                      right: 0
                    }
                  }}
                >
                  A
                </TableSortLabel>
              </StyledTableCell>
            )}

            {/* List Comparison Columns */}
            {activeWorkingList.length === 2 && (
              <>
                <StyledTableCell width={45} align="center">
                  <TableSortLabel
                    active={orderBy === 'listA'}
                    direction={orderBy === 'listA' ? order : 'asc'}
                    onClick={() => handleRequestSort('listA')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiTableSortLabel-icon': {
                        position: 'absolute',
                        right: 0
                      }
                    }}
                  >
                    A
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell width={45} align="center">
                  <TableSortLabel
                    active={orderBy === 'listB'}
                    direction={orderBy === 'listB' ? order : 'asc'}
                    onClick={() => handleRequestSort('listB')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiTableSortLabel-icon': {
                        position: 'absolute',
                        right: 0
                      }
                    }}
                  >
                    B
                  </TableSortLabel>
                </StyledTableCell>
              </>
            )}

            {/* Standard Columns */}
            {/*
              Sortierbar, und die Sortierung geht an die API (argus-platform #192).
              Vorher trugen diese Spalten keinen Schalter -- und der lokale
              Sortierer kannte ohnehin nur die Listenspalten, alles andere gab er
              unveraendert zurueck. Ein Kopf, der sortiert aussieht und nichts tut,
              waere hier der schlechtere Zustand.
            */}
            <StyledTableCell width={50}></StyledTableCell>
            <StyledTableCell>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}
              >
                {t('dataCatalog.table.columns.parameter')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell>{t('dataCatalog.table.columns.tags')}</StyledTableCell>
            <StyledTableCell width={200}>
              <TableSortLabel
                active={orderBy === 'frequency'}
                direction={orderBy === 'frequency' ? order : 'asc'}
                onClick={() => handleRequestSort('frequency')}
              >
                {t('dataCatalog.table.columns.frequency')}
              </TableSortLabel>
            </StyledTableCell>
            {omopSpalte && (
              <StyledTableCell>
                <TableSortLabel
                  active={orderBy === 'omopStatus'}
                  direction={orderBy === 'omopStatus' ? order : 'asc'}
                  onClick={() => handleRequestSort('omopStatus')}
                >
                  {t('dataCatalog.table.columns.omopStatus')}
                </TableSortLabel>
              </StyledTableCell>
            )}
            {openehrSpalte && (
              <StyledTableCell>
                <TableSortLabel
                  active={orderBy === 'openehrStatus'}
                  direction={orderBy === 'openehrStatus' ? order : 'asc'}
                  onClick={() => handleRequestSort('openehrStatus')}
                >
                  {t('dataCatalog.table.columns.openehrStatus')}
                </TableSortLabel>
              </StyledTableCell>
            )}
            <StyledTableCell>
              <TableSortLabel
                active={orderBy === 'sensitivity'}
                direction={orderBy === 'sensitivity' ? order : 'asc'}
                onClick={() => handleRequestSort('sensitivity')}
              >
                {t('dataCatalog.table.columns.sensitivity')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell width={150} align="center">{t('dataCatalog.table.columns.actions')}</StyledTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/*
            Laden, leer und Fehler muessen drei sichtbar verschiedene Zustaende
            sein (argus-platform #191). Vorher waren Laden und Leer dieselbe
            Ansicht: eine Tabelle ohne Zeilen. Genau daran ist am 2026-08-24 eine
            Fehlersuche vorbeigelaufen -- gemeldet wurde "keine Parameter",
            waehrend im Proxy-Log erfolgreiche Abrufe standen.
          */}
          {laedt && Array.from({ length: skelettzeilen }).map((_, i) => (
            <TableRow key={`skelett-${i}`}>
              <TableCell colSpan={spaltenzahl}>
                <Skeleton variant="text" height={32} />
              </TableCell>
            </TableRow>
          ))}

          {/* Noch keine Zeile, aber die Antwort ist unterwegs: weiter Skelett
              zeigen statt eine Aussage zu treffen, die noch niemand belegen
              kann. */}
          {!laedt && nachladen && parameters.length === 0 &&
            Array.from({ length: skelettzeilen }).map((_, i) => (
              <TableRow key={`nachlade-skelett-${i}`}>
                <TableCell colSpan={spaltenzahl}>
                  <Skeleton variant="text" height={32} />
                </TableCell>
              </TableRow>
            ))}

          {!laedt && !nachladen && parameters.length === 0 && (
            <TableRow>
              <TableCell colSpan={spaltenzahl} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="textSecondary">
                  {leerText || t('dataCatalog.table.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!laedt && parameters.map((param) => (
            <ParameterRow
              key={param.id}
              param={param}
              nameView={nameView}
              isExpanded={expandedRows.includes(param.id)}
              onRowClick={onRowClick}
              onFavoriteToggle={onFavoriteToggle}
              activeWorkingList={activeWorkingList}
              parameterLists={parameterLists}
              getParameterListStatus={getParameterListStatus}
              isParameterInAnyList={isParameterInAnyList}
              handleRemoveFromList={handleRemoveFromList}
              handleAddToList={handleAddToList}
              handleOpenListDialog={handleOpenListDialog}
              allTagsWithStats={allTagsWithStats}
              getIconComponent={getIconComponent}
              getPrimaryDescription={getPrimaryDescription}
              subtlePulse={subtlePulse}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataCatalogTable;
